import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { HistoryPanel, escapeHtml as esc, openPrintWindow } from '@/components/documents';
import { ActionDialog, type ActionField } from '@/components/hr/ActionDialog';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import {
  ErrorNote,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  Select,
  Table,
  TextArea,
  errorMessage,
  money,
  rate,
  shortDate,
  todayIso,
} from '@/components/ui';
import { formatMoney, formatNumber } from '@/format';
import {
  LEFT_REASON_TEXT,
  PAY_METHOD_TEXT,
  amountInWords,
  employeeLabel,
  hrGet,
  useEmployeeOptions,
  useHrInvalidation,
  useHrLookups,
  type PaymentMethod,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { useScreenIntent } from '@/stores/shellNav';
import './hr-admin.css';

const PAGE_SIZE = 100;

type SettleState = 'PENDING' | 'APPROVED' | 'POSTED' | 'PAID' | 'REJECTED' | 'REVERSED';

interface SettlementRow {
  id: string;
  documentNo: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  farmCode: string | null;
  leftReason: string | null;
  leftOn: string;
  payFrom: string;
  /** Pay figures are left out for anyone who cannot see pay. */
  settlementEarnings?: string | null;
  settlementDeductions?: string | null;
  settlementNet?: string | null;
  status: string;
  approval: string;
  state: SettleState;
  paidOn: string | null;
  raisedByYou: boolean;
  version: number;
}

interface SettlementLine {
  id: string;
  code: string;
  label: string;
  kind: 'EARNING' | 'DEDUCTION' | 'EMPLOYER';
  quantity: string | null;
  payRate?: string | null;
  payAmount?: string | null;
  isManual: boolean;
  note: string | null;
}

interface SettlementDetail extends SettlementRow {
  employeeNameUr: string | null;
  designation: string | null;
  farm: string;
  joinedOn: string;
  notes: string | null;
  decisionNote: string | null;
  paidMethod: PaymentMethod | null;
  paidReference: string | null;
  reversalReason: string | null;
  lines: SettlementLine[];
  stillOwed: Array<{ documentNo: string; outstandingAmount?: string | null }>;
}

/** Pay items that can be typed onto a settlement by hand. */
const TYPED_IN_CODES = ['NOTICE_PAY', 'NOTICE_DED', 'ARREARS'];

const FILTERS: Array<{ value: SettleState; label: string }> = [
  { value: 'PENDING', label: 'Waiting for a decision' },
  { value: 'APPROVED', label: 'Approved — to post' },
  { value: 'POSTED', label: 'Posted — to pay' },
  { value: 'PAID', label: 'Paid' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'REVERSED', label: 'Reversed' },
];

type DialogKind = 'addLine' | 'approve' | 'reject' | 'post' | 'paid' | 'reverse';
type Dialog = { kind: DialogKind; row: SettlementRow } | null;

function reasonText(reason: string | null): string {
  return reason ? (LEFT_REASON_TEXT[reason] ?? reason) : '—';
}

/** The settlement on paper: who, why, every line, the net in words, and
 *  space for the employee, HR and Accounts to sign. */
function printSettlement(d: SettlementDetail) {
  const earnings = d.lines.filter((l) => l.kind === 'EARNING');
  const deductions = d.lines.filter((l) => l.kind === 'DEDUCTION');
  const table = (title: string, rows: SettlementLine[], total: string | null | undefined) => `
    <table><thead><tr><th>${esc(title)}</th><th class="num">Rs</th></tr></thead>
    <tbody>${
      rows.length
        ? rows
            .map(
              (l) =>
                `<tr><td>${esc(l.label)}${l.quantity ? ` <span style="color:#555">(${esc(formatNumber(l.quantity))})</span>` : ''}${
                  l.note ? `<br><span style="color:#555;font-size:0.8em">${esc(l.note)}</span>` : ''
                }</td><td class="num">${esc(formatMoney(l.payAmount))}</td></tr>`,
            )
            .join('')
        : '<tr><td colspan="2" style="color:#555">None</td></tr>'
    }</tbody>
    <tfoot><tr><th>Total</th><th class="num">${esc(formatMoney(total))}</th></tr></tfoot></table>`;
  const owed = d.stillOwed.filter((o) => o.outstandingAmount && Number(o.outstandingAmount) > 0);
  const facts: Array<[string, string]> = [
    ['Name', `${d.employeeCode} — ${d.employeeName}`],
    ['Designation', d.designation ?? '—'],
    ['Farm', d.farm],
    ['Joined', shortDate(d.joinedOn)],
    ['Left', shortDate(d.leftOn)],
    ['Why they left', reasonText(d.leftReason)],
    ['Pay due from', shortDate(d.payFrom)],
    ['State', stateLabel(d.state)],
  ];
  openPrintWindow({
    title: `Final settlement ${d.documentNo}`,
    css: `
      table.facts th { width: 11rem; background: #f5f5f5; }
      .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; margin-block-start: 1rem; }
      .cols th { background: #f3f3f3; }
      .net { margin-block-start: 0.8rem; padding: 0.5rem 0.7rem; border: 2px solid #111; display: flex; justify-content: space-between; font-weight: 700; }
      .words { font-style: italic; font-size: 0.85rem; margin-block-start: 0.25rem; }
      .signs { display: flex; justify-content: space-between; gap: 2rem; margin-block-start: 3rem; }
      .signs span { border-top: 1px solid #111; padding-top: 0.2rem; min-width: 10rem; text-align: center; font-size: 0.8rem; }`,
    body: `<h1>Final settlement — ${esc(d.documentNo)}</h1>
      <table class="facts">${facts.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
      <div class="cols">
        ${table('Earnings', earnings, d.settlementEarnings)}
        ${table('Deductions', deductions, d.settlementDeductions)}
      </div>
      <div class="net"><span>Net payable</span><span>${esc(formatMoney(d.settlementNet))}</span></div>
      ${d.settlementNet ? `<div class="words">${esc(amountInWords(d.settlementNet))}</div>` : ''}
      ${owed.length ? `<p style="font-size:0.85rem">Still owed after this settlement: ${owed.map((o) => `${esc(o.documentNo)} ${esc(formatMoney(o.outstandingAmount))}`).join(' · ')}</p>` : ''}
      ${d.notes ? `<p style="font-size:0.85rem">Notes: ${esc(d.notes)}</p>` : ''}
      <div class="signs"><span>Employee (signature / thumb)</span><span>HR</span><span>Accounts</span></div>`,
  });
}

/**
 * Final settlement ("full and final") for someone who has left: pay due up to
 * the last day, leave and gratuity where the rules pay them, notice pay either
 * way, less whatever they still owe. Raised by one person, approved by
 * another, posted by the Managing Director, then paid.
 */
export function Settlements() {
  const intent = useScreenIntent('hr-settle');
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const [state, setState] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(intent?.recordId ?? null);
  const [tab, setTab] = useState('details');
  const [creating, setCreating] = useState(intent?.action === 'create');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<{ id: string; items: string[] } | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'settlements', state, skip],
    queryFn: () =>
      hrGet<{ total: number; items: SettlementRow[] }>('/api/v1/hr/settlements', { state, skip, take: PAGE_SIZE }),
  });
  const rows = list.data?.items ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const detail = useQuery({
    queryKey: ['hr', 'settlements', 'detail', selectedKey],
    queryFn: () => hrGet<SettlementDetail>(`/api/v1/hr/settlements/${selectedKey}`),
    enabled: selectedKey !== null,
  });

  function done(message: string) {
    invalidate();
    setProblem(null);
    setNote(message);
  }

  const recalc = useMutation({
    mutationFn: (row: SettlementRow) =>
      api.post<{ id: string; warnings: string[] }>(`/api/v1/hr/settlements/${row.id}/calculate`),
    onSuccess: (r, row) => {
      setWarnings({ id: row.id, items: r.warnings ?? [] });
      done(`${row.documentNo} worked out again.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not work it out')),
  });

  const remove = useMutation({
    mutationFn: (row: SettlementRow) => api.delete(`/api/v1/hr/settlements/${row.id}?version=${row.version}`),
    onSuccess: (_, row) => {
      setSelectedKey(null);
      done(`${row.documentNo} deleted.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not delete it')),
  });

  const removeLine = useMutation({
    mutationFn: (line: SettlementLine) => api.delete(`/api/v1/hr/settlements/lines/${line.id}`),
    onSuccess: (_, line) => done(`${line.label} removed and the settlement worked out again.`),
    onError: (e) => setProblem(errorMessage(e, 'Could not remove the line')),
  });

  async function print(row: SettlementRow) {
    try {
      const d = await hrGet<SettlementDetail>(`/api/v1/hr/settlements/${row.id}`);
      printSettlement(d);
      await api.post(`/api/v1/hr/settlements/${row.id}/printed`, { what: `Final settlement ${d.documentNo}` });
    } catch (e) {
      setProblem(errorMessage(e, 'Could not print it'));
    }
  }

  function rowActions(row: SettlementRow): DesktopAction[] {
    const waiting = row.state === 'PENDING';
    return [
      {
        label: 'Work out again',
        icon: 'calculate',
        onSelect: () => recalc.mutate(row),
        allowed: can('hr.settlement.update'),
        enabled: waiting,
        reason: 'Only one still waiting for a decision can be worked out again',
      },
      {
        label: 'Add a line',
        icon: 'add',
        onSelect: () => setDialog({ kind: 'addLine', row }),
        allowed: can('hr.settlement.update'),
        enabled: waiting,
        reason: 'Only one still waiting for a decision can be changed',
      },
      {
        label: 'Approve',
        icon: 'approve',
        onSelect: () => setDialog({ kind: 'approve', row }),
        allowed: can('hr.settlement.approve'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever started it cannot approve it' : 'Only one waiting for a decision can be approved',
        separatorBefore: true,
      },
      {
        label: 'Reject',
        icon: 'reject',
        onSelect: () => setDialog({ kind: 'reject', row }),
        allowed: can('hr.settlement.reject'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever started it cannot reject it' : 'Only one waiting for a decision can be rejected',
      },
      {
        label: 'Post',
        icon: 'post',
        onSelect: () => setDialog({ kind: 'post', row }),
        allowed: can('hr.settlement.post'),
        enabled: row.state === 'APPROVED',
        reason: 'It must be approved and not yet posted',
      },
      {
        label: 'Record paid',
        icon: 'paid',
        onSelect: () => setDialog({ kind: 'paid', row }),
        allowed: can('hr.settlement.update'),
        enabled: row.state === 'POSTED',
        reason: 'It must be posted and not yet paid',
        separatorBefore: true,
      },
      {
        label: 'Reverse',
        icon: 'reverse',
        danger: true,
        onSelect: () => setDialog({ kind: 'reverse', row }),
        allowed: can('hr.settlement.reverse'),
        enabled: row.state === 'POSTED',
        reason: row.state === 'PAID' ? 'It has been paid, so it cannot be reversed' : 'Only a posted settlement can be reversed',
      },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        shortcut: 'Del',
        onSelect: () => {
          if (window.confirm(`Delete ${row.documentNo}? It has not been approved, so nothing else changes.`)) remove.mutate(row);
        },
        allowed: can('hr.settlement.delete'),
        enabled: waiting,
        reason: 'Only one not yet approved can be deleted',
      },
      {
        label: 'Print',
        icon: 'print',
        onSelect: () => void print(row),
        allowed: can('hr.settlement.print'),
        separatorBefore: true,
      },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: 'Start settlement',
      icon: 'add',
      primary: true,
      shortcut: 'Ins',
      onSelect: () => setCreating(true),
      allowed: can('hr.settlement.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Approve',
            icon: 'approve' as const,
            onSelect: () => {},
            allowed: can('hr.settlement.approve'),
            enabled: false,
            reason: 'Choose one first',
            separatorBefore: true,
          },
        ]),
  ];

  const columns: Array<GridColumn<SettlementRow>> = [
    {
      key: 'doc',
      header: 'Number',
      width: 150,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind="settlement" size={14} />
          <span className="mono">{r.documentNo}</span>
        </span>
      ),
    },
    { key: 'person', header: 'Person', width: 210, render: (r) => `${r.employeeCode} — ${r.employeeName}` },
    { key: 'farm', header: 'Farm', width: 80, render: (r) => <span className="dim">{r.farmCode ?? 'Head office'}</span> },
    { key: 'left', header: 'Left on', width: 100, render: (r) => shortDate(r.leftOn) },
    { key: 'why', header: 'Why', width: 110, render: (r) => reasonText(r.leftReason) },
    { key: 'from', header: 'Pay due from', width: 110, render: (r) => shortDate(r.payFrom) },
    { key: 'earn', header: 'Earnings', align: 'end', width: 110, render: (r) => money(r.settlementEarnings) },
    { key: 'ded', header: 'Deductions', align: 'end', width: 110, render: (r) => money(r.settlementDeductions) },
    { key: 'net', header: 'Net', align: 'end', width: 110, render: (r) => <strong>{money(r.settlementNet)}</strong> },
    { key: 'state', header: 'State', width: 180, render: (r) => <StatePill state={r.state} /> },
  ];

  if (creating) {
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          <StartSettlementForm
            presetEmployeeId={intent?.employeeId}
            onCancel={() => setCreating(false)}
            onSaved={(message, id) => {
              setCreating(false);
              setSelectedKey(id);
              setTab('lines');
              done(message);
            }}
          />
        </div>
      </div>
    );
  }

  const d = detail.data && detail.data.id === selectedKey ? detail.data : null;
  const canEditLines = d !== null && d.state === 'PENDING' && can('hr.settlement.update');
  const shownWarnings = warnings && d && warnings.id === d.id ? warnings.items : [];

  const linesTable = (title: string, lines: SettlementLine[], total: string | null | undefined) => (
    <section>
      <h3 className="formsection">{title}</h3>
      <Table
        count={lines.length}
        empty="None."
        head={
          <tr>
            <th>What</th>
            <th className="num">Days / rate</th>
            <th className="num">Amount</th>
            {canEditLines && <th aria-label="Remove" />}
          </tr>
        }
      >
        {lines.map((l) => (
          <tr key={l.id}>
            <td>
              {l.label}
              {l.isManual && <span className="pill">Typed in</span>}
              {l.note && <div className="dim">{l.note}</div>}
            </td>
            <td className="num dim">
              {l.quantity ? formatNumber(l.quantity) : ''}
              {l.quantity && l.payRate ? ` × ${rate(l.payRate)}` : ''}
            </td>
            <td className="num">{money(l.payAmount)}</td>
            {canEditLines && (
              <td>
                {l.isManual && (
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost btn--withicon"
                    disabled={removeLine.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove "${l.label}" from ${d!.documentNo}?`)) removeLine.mutate(l);
                    }}
                  >
                    <AppIcon kind="remove" size={14} />
                    Remove
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
        {lines.length > 0 && (
          <tr>
            <th>Total</th>
            <td />
            <th className="num">{money(total)}</th>
            {canEditLines && <td />}
          </tr>
        )}
      </Table>
    </section>
  );

  const owedLeft = d ? d.stillOwed.filter((o) => o.outstandingAmount === undefined || o.outstandingAmount === null || Number(o.outstandingAmount) > 0) : [];

  const tabs: DetailTab[] = d
    ? [
        {
          id: 'details',
          label: 'Details',
          icon: 'details',
          content: (
            <div className="stack">
              {shownWarnings.length > 0 && (
                <div className="hrwarnbox" role="note">
                  <strong>Worked out with these warnings:</strong>
                  <ul>
                    {shownWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
              <dl className="detaillist">
                <dt>Person</dt>
                <dd>
                  {d.employeeCode} — {d.employeeName}
                  {d.employeeNameUr && <span className="urdu"> · {d.employeeNameUr}</span>}
                </dd>
                <dt>Designation</dt>
                <dd>{d.designation ?? '—'}</dd>
                <dt>Farm</dt>
                <dd>{d.farm}</dd>
                <dt>Joined · Left</dt>
                <dd>
                  {shortDate(d.joinedOn)} · {shortDate(d.leftOn)}
                </dd>
                <dt>Why they left</dt>
                <dd>{reasonText(d.leftReason)}</dd>
                <dt>Pay due from</dt>
                <dd>{shortDate(d.payFrom)}</dd>
                <dt>State</dt>
                <dd>
                  <StatePill state={d.state} />
                </dd>
                <dt>Net payable</dt>
                <dd>
                  <strong>{money(d.settlementNet)}</strong>
                  {d.settlementNet && <div className="dim">{amountInWords(d.settlementNet)}</div>}
                </dd>
                {d.notes && (
                  <>
                    <dt>Notes</dt>
                    <dd>{d.notes}</dd>
                  </>
                )}
                {d.decisionNote && (
                  <>
                    <dt>Decision note</dt>
                    <dd>{d.decisionNote}</dd>
                  </>
                )}
                {d.paidOn && (
                  <>
                    <dt>Paid</dt>
                    <dd>
                      {shortDate(d.paidOn)}
                      {d.paidMethod ? ` · ${PAY_METHOD_TEXT[d.paidMethod]}` : ''}
                      {d.paidReference ? ` · ${d.paidReference}` : ''}
                    </dd>
                  </>
                )}
                {d.reversalReason && (
                  <>
                    <dt>Reversed because</dt>
                    <dd>{d.reversalReason}</dd>
                  </>
                )}
              </dl>
              {owedLeft.length > 0 && (
                <div className="hrwarnbox">
                  <strong>Still owed on advances and loans</strong>
                  <ul>
                    {owedLeft.map((o) => (
                      <li key={o.documentNo}>
                        {o.documentNo}: {money(o.outstandingAmount)}
                      </li>
                    ))}
                  </ul>
                  <p className="muted">
                    Posting takes what this settlement can cover. Anything left after that can be written off in Advances &amp; Loans.
                  </p>
                </div>
              )}
            </div>
          ),
        },
        {
          id: 'lines',
          label: 'Lines',
          icon: 'payslip',
          content: (
            <div className="stack">
              <div className="hrsettle__lines">
                {linesTable('Earnings', d.lines.filter((l) => l.kind === 'EARNING'), d.settlementEarnings)}
                {linesTable('Deductions', d.lines.filter((l) => l.kind === 'DEDUCTION'), d.settlementDeductions)}
              </div>
              <p>
                <strong>Net payable: {money(d.settlementNet)}</strong>
              </p>
            </div>
          ),
        },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/hr/settlements/${d.id}/history`} /> },
      ]
    : [];

  const waitingCount = rows.filter((r) => r.state === 'PENDING').length;
  const netShown = rows.every((r) => r.settlementNet !== undefined && r.settlementNet !== null)
    ? rows.reduce((t, r) => t + Number(r.settlementNet ?? 0), 0)
    : null;

  return (
    <div className="workbench">
      <SplitView
        id="hr-settlements"
        direction="column"
        initial={400}
        firstLabel="settlements list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <Select
                label="Show"
                value={state}
                onChange={(v) => {
                  setState(v);
                  setSkip(0);
                }}
                options={FILTERS}
                placeholder="Everything"
              />
            </div>
            <ActionBar actions={toolbar} />
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-settlements"
              label="Final settlements"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={(k) => {
                setSelectedKey(k);
                setNote(null);
              }}
              onOpen={(r) => {
                setSelectedKey(r.id);
                setTab('lines');
              }}
              onDelete={(r) => {
                if (r.state === 'PENDING' && can('hr.settlement.delete') && window.confirm(`Delete ${r.documentNo}?`)) remove.mutate(r);
              }}
              actions={rowActions}
              loading={list.isPending}
              empty={state ? `Nothing ${stateLabel(state).toLowerCase()}.` : 'No final settlements yet. Start one when someone is marked as left.'}
            />
            <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a settlement to see its lines and what is still owed."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="settlement" size={14} />
                  <span className="mono">{selected.documentNo}</span> {selected.employeeName}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>{list.data?.total ?? 0} shown</span>
        {waitingCount > 0 && <span>{waitingCount} waiting for a decision</span>}
        {netShown !== null && rows.length > 0 && <span>Net on this page: {money(String(netShown))}</span>}
        <span>{selected ? `Chosen: ${selected.documentNo}` : 'Nothing chosen'}</span>
      </StatusBar>

      {dialog && (
        <SettlementDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onWarnings={(items) => setWarnings({ id: dialog.row.id, items })}
          onDone={done}
        />
      )}
    </div>
  );
}

/** The dialog behind each action that needs more than a click. */
function SettlementDialog({
  dialog,
  onClose,
  onWarnings,
  onDone,
}: {
  dialog: NonNullable<Dialog>;
  onClose: () => void;
  onWarnings: (items: string[]) => void;
  onDone: (message: string) => void;
}) {
  const { row } = dialog;
  const base = `/api/v1/hr/settlements/${row.id}`;
  const lookups = useHrLookups(dialog.kind === 'addLine');
  const components = (lookups.data?.payComponents ?? []).filter(
    (c) => (c.kind === 'EARNING' || c.kind === 'DEDUCTION') && (c.systemCode === null || TYPED_IN_CODES.includes(c.systemCode)),
  );

  const configs: Record<
    DialogKind,
    {
      title: string;
      icon: 'add' | 'approve' | 'reject' | 'post' | 'paid' | 'reverse';
      intro?: string;
      fields: ActionField[];
      initial?: Record<string, string>;
      submit: string;
      danger?: boolean;
      run: (v: Record<string, string>) => Promise<string>;
    }
  > = {
    addLine: {
      title: `Add a line — ${row.documentNo}`,
      icon: 'add',
      intro: 'For notice pay, notice not served, arrears or anything else agreed. The settlement is worked out again straight after.',
      fields: [
        {
          key: 'componentId',
          label: 'What',
          type: 'select',
          required: true,
          options: components.map((c) => ({
            value: c.id,
            label: `${c.nameEn} (${c.kind === 'EARNING' ? 'paid to them' : 'taken from them'})`,
          })),
        },
        { key: 'amount', label: 'Amount', type: 'number', required: true },
        { key: 'note', label: 'Note', type: 'textarea', placeholder: 'e.g. one month’s notice not served' },
      ],
      submit: 'Add line',
      run: async (v) => {
        if (!(Number(v.amount) > 0)) throw new Error('Enter an amount above zero.');
        const r = await api.post<{ warnings?: string[] }>(`${base}/lines`, {
          componentId: v.componentId,
          amount: v.amount,
          note: v.note?.trim() || undefined,
        });
        onWarnings(r?.warnings ?? []);
        return `Line added to ${row.documentNo} and worked out again.`;
      },
    },
    approve: {
      title: `Approve ${row.documentNo}`,
      icon: 'approve',
      intro: `${row.employeeName} — net ${money(row.settlementNet)}. Once approved, the Managing Director can post it.`,
      fields: [{ key: 'comment', label: 'Comment', type: 'textarea' }],
      submit: 'Approve',
      run: async (v) => {
        await api.post(`${base}/approve`, { comment: v.comment?.trim() || undefined });
        return `${row.documentNo} approved. It can now be posted.`;
      },
    },
    reject: {
      title: `Reject ${row.documentNo}`,
      icon: 'reject',
      fields: [{ key: 'comment', label: 'Why', type: 'textarea', required: true }],
      submit: 'Reject',
      danger: true,
      run: async (v) => {
        await api.post(`${base}/reject`, { comment: v.comment });
        return `${row.documentNo} rejected.`;
      },
    },
    post: {
      title: `Post ${row.documentNo}`,
      icon: 'post',
      intro: `Posting makes it final. Advances and loans ${row.employeeName} still owes are recovered from this settlement as far as it covers them, and they drop out of future pay runs. It cannot be changed afterwards, only reversed before it is paid.`,
      fields: [],
      submit: 'Post',
      run: async () => {
        await api.post(`${base}/post`);
        return `${row.documentNo} posted. Record it as paid once the money is handed over.`;
      },
    },
    paid: {
      title: `Record paid — ${row.documentNo}`,
      icon: 'paid',
      intro: `Record that ${money(row.settlementNet)} was paid to ${row.employeeName}.`,
      fields: [
        { key: 'paidOn', label: 'Paid on', type: 'date', required: true },
        {
          key: 'method',
          label: 'How',
          type: 'select',
          required: true,
          options: [
            { value: 'CASH', label: 'Cash' },
            { value: 'BANK', label: 'Bank transfer' },
            { value: 'WALLET', label: 'Mobile wallet' },
          ],
        },
        { key: 'reference', label: 'Voucher, cheque or transfer number' },
      ],
      initial: { paidOn: todayIso(), method: 'CASH' },
      submit: 'Record paid',
      run: async (v) => {
        await api.post(`${base}/paid`, { paidOn: v.paidOn, method: v.method, reference: v.reference?.trim() || undefined });
        return `${row.documentNo} recorded as paid.`;
      },
    },
    reverse: {
      title: `Reverse ${row.documentNo}`,
      icon: 'reverse',
      intro: 'Use this when it was posted by mistake. What was recovered from advances and loans is put back as owed.',
      fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
      submit: 'Reverse',
      danger: true,
      run: async (v) => {
        await api.post(`${base}/reverse`, { reason: v.reason });
        return `${row.documentNo} reversed.`;
      },
    },
  };

  const c = configs[dialog.kind];
  return (
    <ActionDialog
      title={c.title}
      icon={c.icon}
      intro={c.intro}
      fields={c.fields}
      {...(c.initial ? { initial: c.initial } : {})}
      submitLabel={c.submit}
      danger={c.danger ?? false}
      onClose={onClose}
      onSubmit={async (v) => {
        onDone(await c.run(v));
      }}
    />
  );
}

function StartSettlementForm({
  presetEmployeeId,
  onCancel,
  onSaved,
}: {
  presetEmployeeId: string | undefined;
  onCancel: () => void;
  onSaved: (message: string, id: string) => void;
}) {
  const people = useEmployeeOptions('LEFT');
  const [employeeId, setEmployeeId] = useState(presetEmployeeId ?? '');
  const [notes, setNotes] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const chosen = (people.data?.items ?? []).find((e) => e.id === employeeId);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; documentNo: string }>('/api/v1/hr/settlements', {
        employeeId,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (r) => onSaved(`${r.documentNo} started and worked out. Someone else must approve it.`, r.id),
    onError: (e) => setProblem(errorMessage(e, 'Could not start it')),
  });

  return (
    <FormPanel
      title="Start a final settlement"
      icon={<AppIcon kind="settlement" />}
      intro="Choose someone already marked as left. Their pay due, leave, gratuity and anything they still owe are worked out straight away; you can add notice pay or other lines after."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!employeeId) return setProblem('Choose the person.');
          setProblem(null);
          save.mutate();
        }}
      >
        <FormGroup title="Who" columns={2}>
          <Select
            label="Person who left"
            required
            value={employeeId}
            onChange={setEmployeeId}
            options={(people.data?.items ?? []).map((e) => ({ value: e.id, label: employeeLabel(e) }))}
            hint={
              people.data && people.data.items.length === 0
                ? 'Nobody is marked as left. Mark someone as left in Employees first.'
                : chosen?.leftOn
                  ? `Left on ${shortDate(chosen.leftOn)}`
                  : undefined
            }
          />
          <TextArea label="Notes" value={notes} onChange={setNotes} rows={2} placeholder="e.g. agreed to pay one month’s notice" />
        </FormGroup>
        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Working it out…' : 'Start and work out'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
